import type { ComponentProps } from 'react';
import {
  type FieldPath,
  type FieldPathValue,
  type FieldValues,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import CheckboxItem from '../UI/CheckboxRow';

type ArrayItem<T> = T extends (infer U)[] ? U : never;

type FormCheckboxItemProps<
  TFormValues extends FieldValues,
  TName extends FieldPath<TFormValues>,
  TItem extends ArrayItem<FieldPathValue<TFormValues, TName>>,
> = Omit<ComponentProps<typeof CheckboxItem>, 'value' | 'onValueChange'> & {
  name: TName;
  item: TItem;
  getItemId(item: TItem): string;
};

export default function FormCheckboxItem<
  TFormValues extends FieldValues,
  TName extends FieldPath<TFormValues>,
  TItem extends ArrayItem<FieldPathValue<TFormValues, TName>>,
>({
  name,
  item,
  getItemId,
  ...checkboxProps
}: FormCheckboxItemProps<TFormValues, TName, TItem>) {
  const { control, setValue } = useFormContext<TFormValues>();

  const selectedValue = useWatch<TFormValues, TName>({
    control,
    name,
    defaultValue: [] as FieldPathValue<TFormValues, TName>,
  });

  const selectedItems = Array.isArray(selectedValue)
    ? (selectedValue as TItem[])
    : ([] as TItem[]);

  const itemId = getItemId(item);

  const isSelected = selectedItems.some(
    (selectedItem) => getItemId(selectedItem) === itemId,
  );

  const onValueChange = () => {
    const nextItems = isSelected
      ? selectedItems.filter(
          (selectedItem) => getItemId(selectedItem) !== itemId,
        )
      : [...selectedItems, item];

    setValue(name, nextItems as FieldPathValue<TFormValues, TName>, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  return (
    <CheckboxItem
      {...checkboxProps}
      variant="info"
      value={isSelected}
      onValueChange={onValueChange}
    />
  );
}
